import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, X } from 'lucide-react';
import { AiOutlineStock } from 'react-icons/ai';
import { IoArrowForwardOutline } from 'react-icons/io5';
import knowhowLogo from '../../assets/knowhow-logo.svg';
import knowhowMark from '../../assets/knowhow-mark.svg';
import { NavData, SecondaryNavData } from '../Constant';
import SessionsListSection from './SessionsListSection';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { StyledSideBar, MobileOpenBtn, SideBarBackdrop, ReponseMenu, MenuBtn, StyleItem } from './SideBar.styles';
import agentService from '../../services/agentService';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_AGENT, DEFAULT_AGENT_ID } from '../../constants/defaultAgent';

const MOBILE_MQ = '(max-width: 1199px)';

const SideBar = () => {
  const [sideBar, setSideBar] = useState(true);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches,
  );
  const [agentData, setAgentData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState({
    label: 'All',
    value: '',
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { user, onLogout } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
  }));

  const getAllAgentData = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentData([
        { agent_name: 'All', id: null },
        { agent_name: DEFAULT_AGENT.label, id: DEFAULT_AGENT_ID },
        ...getAllAgentData?.data?.agents,
      ]);
    }
  }, [getAllAgentData.isSuccess, getAllAgentData?.data?.agents]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => {
      setIsMobile(mq.matches);
      if (!mq.matches) setSideBar(true);
      else setSideBar(true); // closed on mobile (inverted drawer)
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (sideBar) {
      document.body.classList.add('sidebar-active');
    } else {
      document.body.classList.remove('sidebar-active');
    }
  }, [sideBar]);

  // Close mobile drawer after navigation
  useEffect(() => {
    if (isMobile && !sideBar) setSideBar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (isMobile && !sideBar) {
      document.body.classList.add('mobile-drawer-open');
    } else {
      document.body.classList.remove('mobile-drawer-open');
    }
    return () => document.body.classList.remove('mobile-drawer-open');
  }, [isMobile, sideBar]);

  const isActive = path => {
    const href = `/${path}`;
    if (path === 'dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/chat' || location.pathname.startsWith('/chat/');
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const displayName = user?.first_name || 'User';
  const mobileDrawerOpen = isMobile && !sideBar;
  const mobileDrawerClosed = isMobile && sideBar;

  const closeMobile = () => {
    if (isMobile) setSideBar(true);
  };

  return (
    <>
      {mobileDrawerClosed ? (
        <MobileOpenBtn type="button" onClick={() => setSideBar(false)} title="Open menu" aria-label="Open menu">
          <img src={knowhowMark} alt="" width={22} height={22} />
        </MobileOpenBtn>
      ) : null}

      {mobileDrawerOpen ? <SideBarBackdrop onClick={closeMobile} aria-hidden="true" /> : null}

      <StyledSideBar $sideBar={sideBar} $mobileOpen={mobileDrawerOpen}>
        <div className="nav-wrapper">
          <div className="nav-header">
            <div className="logo">
              <Link to="/dashboard" onClick={closeMobile}>
                <img className="logo-lg" src={knowhowLogo} width="140" alt="KnowHow" />
                <img className="logo-sm" src={knowhowMark} width="24" alt="KnowHow" />
              </Link>
            </div>
            <button
              type="button"
              className="menu-btn"
              onClick={() => setSideBar(open => !open)}
              title={isMobile ? 'Close menu' : sideBar ? 'Collapse' : 'Expand'}>
              {isMobile ? (
                <X size={18} />
              ) : sideBar ? (
                <PanelLeftClose size={18} />
              ) : (
                <PanelLeftOpen size={18} />
              )}
            </button>
          </div>

          <div className="scroll-area">
            <ul className="nav-links">
              {NavData.map(elem => (
                <li key={elem.path}>
                  <NavLink
                    to={`/${elem.path}`}
                    title={!sideBar && !isMobile ? elem.name : undefined}
                    onClick={closeMobile}>
                    {isActive(elem.path) ? <span className="active-bar" /> : null}
                    <img src={elem.img} alt="" className="icon" />
                    <span className="label">{elem.name}</span>
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="chat-history">
              <div className="head">
                <span className="subheading">Chats</span>
                <ReponseMenu menuButton={<MenuBtn type="button">{selectedAgent?.label || 'All'}</MenuBtn>}>
                  {agentData?.map((data, index) => (
                    <StyleItem
                      key={data.id ?? index}
                      onClick={() => {
                        setSelectedAgent({ label: data?.agent_name, value: data?.id });
                      }}>
                      <div className="text">{data?.agent_name}</div>
                    </StyleItem>
                  ))}
                </ReponseMenu>
              </div>
              <div className="sessions-wrap">
                <SessionsListSection selectedAgent={selectedAgent} />
              </div>
            </div>
          </div>
        </div>

        <div className="sidebar-footer">
          <ul className="nav-links nav2">
            {SecondaryNavData.map(elem => (
              <li key={elem.path}>
                <NavLink
                  to={`/${elem.path}`}
                  title={!sideBar && !isMobile ? elem.name : undefined}
                  onClick={closeMobile}>
                  {isActive(elem.path) ? <span className="active-bar" /> : null}
                  <AiOutlineStock className="icon" />
                  <span className="label">{elem.name}</span>
                </NavLink>
              </li>
            ))}
            <li>
              <button
                type="button"
                className="btn-logout"
                onClick={onLogout}
                title={!sideBar && !isMobile ? 'Logout' : undefined}>
                <LogOut className="icon" />
                <span className="label">Logout</span>
              </button>
            </li>
          </ul>

          <div
            className="profile-holder"
            onClick={() => {
              closeMobile();
              navigate('/settings');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                closeMobile();
                navigate('/settings');
              }
            }}
            role="button"
            tabIndex={0}>
            <div className="profile-img">
              <span className="text">{initial}</span>
            </div>
            <div className="profile-info">
              <span className="name">{displayName}</span>
              <span className="text">Free plan</span>
            </div>
            <IoArrowForwardOutline className="icon-arrow" />
          </div>
        </div>
      </StyledSideBar>
    </>
  );
};

export default SideBar;
